import { PaginationControls } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { AuditLogService } from "@/lib/api/audit-log.service";
import { useAuth } from "@/lib/auth";
import { roleDisplayNames } from "@/lib/permissions";
import type { AuditLogEntry, SystemRole } from "@/lib/schema";
import { cn } from "@/lib/utils";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Download, FileText, Filter, RefreshCw, Search } from "lucide-react";
import { useEffect, useState } from "react";

const greenBadge =
  "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
const blueBadge =
  "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
const redBadge = "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
const purpleBadge =
  "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400";
const emeraldBadge =
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
const amberBadge =
  "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
const mutedBadge = "bg-muted text-muted-foreground";

const actionTypeColors: Record<string, string> = {
  // App user
  app_user_force_logout: amberBadge,
  app_user_note_added: blueBadge,
  app_user_trial_granted: blueBadge,
  // Coach
  coach_invited: greenBadge,
  coach_activated: greenBadge,
  coach_updated: blueBadge,
  coach_deleted: redBadge,
  coach_restored: greenBadge,
  // Content
  content_created: greenBadge,
  content_updated: blueBadge,
  content_submitted: blueBadge,
  content_changes_requested: amberBadge,
  content_approved: greenBadge,
  content_published: emeraldBadge,
  content_withdrawn: amberBadge,
  content_archived: mutedBadge,
  content_restored: greenBadge,
  content_deleted: redBadge,
  content_version_created: blueBadge,
  // Subscription
  subscription_cancelled: redBadge,
  subscription_paused: amberBadge,
  subscription_resumed: greenBadge,
  subscription_trial_extended: blueBadge,
  subscription_complimentary_granted: greenBadge,
  subscription_webhook_event: mutedBadge,
  failed_payment_status_updated: amberBadge,
  // Admin user / internal users
  admin_user_role_updated: purpleBadge,
  admin_user_role_removed: amberBadge,
  admin_user_suspended: redBadge,
  admin_user_restored: greenBadge,
  admin_user_deleted: redBadge,
  admin_user_email_changed: amberBadge,
  admin_user_password_changed: amberBadge,
  admin_invitation_created: greenBadge,
  admin_invitation_resent: blueBadge,
  admin_invitation_cancelled: amberBadge,
  admin_invitation_accepted: greenBadge,
  // Taxonomy
  category_created: greenBadge,
  category_updated: blueBadge,
  category_deleted: redBadge,
  category_archived: mutedBadge,
  category_restored: greenBadge,
  category_translation_deleted: amberBadge,
  tag_created: greenBadge,
  tag_updated: blueBadge,
  tag_deleted: redBadge,
  // Affiliate / partner
  affiliate_created: greenBadge,
  affiliate_updated: blueBadge,
  affiliate_suspended: redBadge,
  affiliate_restored: greenBadge,
  affiliate_deleted: redBadge,
  partner_request_approved: greenBadge,
  partner_request_rejected: redBadge,
};

const actionTypeLabels: Record<string, string> = {
  // App user
  app_user_force_logout: "App User Force Logout",
  app_user_note_added: "App User Note Added",
  app_user_trial_granted: "App User Trial Granted",
  // Coach
  coach_invited: "Coach Invited",
  coach_activated: "Coach Activated",
  coach_updated: "Coach Updated",
  coach_deleted: "Coach Removed",
  coach_restored: "Coach Restored",
  // Content
  content_created: "Content Created",
  content_updated: "Content Updated",
  content_submitted: "Content Submitted",
  content_changes_requested: "Changes Requested",
  content_approved: "Content Approved",
  content_published: "Content Published",
  content_withdrawn: "Content Withdrawn",
  content_archived: "Content Archived",
  content_restored: "Content Restored",
  content_deleted: "Content Deleted",
  content_version_created: "Version Created",
  // Subscription
  subscription_cancelled: "Subscription Cancelled",
  subscription_paused: "Subscription Paused",
  subscription_resumed: "Subscription Resumed",
  subscription_trial_extended: "Subscription Trial Extended",
  subscription_complimentary_granted: "Complimentary Access Granted",
  subscription_webhook_event: "Subscription Webhook Event",
  failed_payment_status_updated: "Failed Payment Status Updated",
  // Admin user / internal users
  admin_user_role_updated: "Role Granted",
  admin_user_role_removed: "Role Removed",
  admin_user_suspended: "User Suspended",
  admin_user_restored: "User Restored",
  admin_user_deleted: "User Deleted",
  admin_user_email_changed: "Email Changed",
  admin_user_password_changed: "Password Changed",
  admin_invitation_created: "Invitation Sent",
  admin_invitation_resent: "Invitation Resent",
  admin_invitation_cancelled: "Invitation Cancelled",
  admin_invitation_accepted: "Invitation Accepted",
  // Taxonomy
  category_created: "Category Created",
  category_updated: "Category Updated",
  category_deleted: "Category Deleted",
  category_archived: "Category Archived",
  category_restored: "Category Restored",
  category_translation_deleted: "Category Translation Removed",
  tag_created: "Tag Created",
  tag_updated: "Tag Updated",
  tag_deleted: "Tag Deleted",
  // Affiliate / partner
  affiliate_created: "Affiliate Created",
  affiliate_updated: "Affiliate Updated",
  affiliate_suspended: "Affiliate Suspended",
  affiliate_restored: "Affiliate Restored",
  affiliate_deleted: "Affiliate Removed",
  partner_request_approved: "Partner Request Approved",
  partner_request_rejected: "Partner Request Rejected",
};

function formatActionLabel(actionType: string): string {
  return (
    actionTypeLabels[actionType] ||
    actionType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function formatEntityType(entityType: string): string {
  return entityType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const PAGE_SIZE = 8;
const SEARCH_DEBOUNCE_MS = 300;

const ENTITY_TYPES = [
  "user",
  "coach",
  "content",
  "subscription",
  "failed_payment",
  "invitation",
  "category",
  "tag",
  "affiliate",
  "partner_request",
];

function browserLang(): string {
  const l = (navigator.language || "en").slice(0, 2).toLowerCase();
  return ["en", "pt", "es"].includes(l) ? l : "en";
}

export default function AuditLogs() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const hasAccess =
    user?.roles?.includes("super_admin") || user?.roles?.includes("admin");

  useEffect(() => {
    const t = setTimeout(
      () => setSearchQuery(searchInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [actionFilter, entityFilter, searchQuery, startDate, endDate]);

  const offset = (currentPage - 1) * PAGE_SIZE;
  const filters = {
    actionType: actionFilter === "all" ? undefined : actionFilter,
    entityType: entityFilter === "all" ? undefined : entityFilter,
    search: searchQuery || undefined,
    startDate: startDate ? `${startDate}T00:00:00` : undefined,
    endDate: endDate ? `${endDate}T23:59:59` : undefined,
    lang: browserLang(),
  };

  const { data, isLoading, refetch, isFetching, isPlaceholderData } = useQuery({
    queryKey: [
      "/api/audit-logs",
      {
        limit: PAGE_SIZE,
        offset,
        actionType: actionFilter,
        entityType: entityFilter,
        search: searchQuery,
        startDate,
        endDate,
      },
    ],
    queryFn: () =>
      AuditLogService.findAll({ limit: PAGE_SIZE, offset, ...filters }),
    enabled: !!hasAccess,
    placeholderData: keepPreviousData,
  });

  const entries: AuditLogEntry[] = data?.entries ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const startIndex = total === 0 ? 0 : offset;
  const endIndex = Math.min(offset + entries.length, total);

  const exportCsv = async () => {
    setExporting(true);
    let all: AuditLogEntry[];
    try {
      // Pin the upper bound so rows logged mid-export can't shift the offset
      // batches (rows are newest-first) and duplicate/skip entries.
      all = await AuditLogService.findAllMatching({
        ...filters,
        endDate: filters.endDate ?? new Date().toISOString(),
      });
    } catch {
      toast({
        title: "Export failed",
        description: "Could not fetch all audit log entries. Try again.",
        variant: "destructive",
      });
      return;
    } finally {
      setExporting(false);
    }
    const header = [
      "Timestamp (UTC)",
      "User",
      "Role",
      "Action",
      "Entity Type",
      "Entity",
      "Description",
      "Source",
    ];
    const rows = all.map((l) => [
      l.timestamp,
      l.actorUsername ?? l.actorId ?? "",
      l.actorRole ?? "system",
      l.actionType,
      l.entityType,
      l.entityLabel ?? l.entityId ?? "",
      l.description,
      l.source ?? "",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-logs-${format(new Date(), "yyyy-MM-dd-HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!hasAccess) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Access Denied</h3>
          <p className="text-muted-foreground mt-1">
            Audit logs are only available to Admins
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight">
            Audit Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all administrative actions for compliance and security
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={total === 0 || exporting}
            data-testid="button-export-logs"
            title="Export all entries matching the current filters as CSV"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? "Exporting…" : "Export"}
          </Button>
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="button-refresh-logs"
          >
            <RefreshCw
              className={cn("h-4 w-4 mr-2", isFetching && "animate-spin")}
            />
            Refresh
          </Button>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-4 lg:flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search user, action, or entity…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
            data-testid="input-search-logs"
          />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger
            className="w-[220px]"
            data-testid="select-action-filter"
          >
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Action Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {Object.keys(actionTypeLabels)
              .sort()
              .map((action) => (
                <SelectItem key={action} value={action}>
                  {formatActionLabel(action)}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger
            className="w-[190px]"
            data-testid="select-entity-filter"
          >
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Entity Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            {ENTITY_TYPES.map((et) => (
              <SelectItem key={et} value={et}>
                {formatEntityType(et)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-[150px]"
            aria-label="From date"
            data-testid="input-start-date"
          />
          <span className="text-muted-foreground text-sm">to</span>
          <Input
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-[150px]"
            aria-label="To date"
            data-testid="input-end-date"
          />
        </div>
      </div>
      <div
        className={cn(
          "rounded-md border transition-opacity",
          isPlaceholderData && "opacity-60",
        )}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(7)].map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  No audit logs found
                </TableCell>
              </TableRow>
            ) : (
              entries.map((log) => (
                <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                  <TableCell
                    className="text-sm text-muted-foreground whitespace-nowrap"
                    title={`${new Date(log.timestamp).toISOString()} (UTC)`}
                  >
                    {format(new Date(log.timestamp), "MMM d, yyyy HH:mm")}
                  </TableCell>
                  <TableCell className="font-medium">
                    {log.actorUsername ||
                      (log.actorId ? `User ${log.actorId.slice(0, 8)}…` : "—")}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {log.actorRole
                        ? (roleDisplayNames[log.actorRole as SystemRole] ??
                          log.actorRole)
                        : "System"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={cn(
                        "text-xs",
                        actionTypeColors[log.actionType] || mutedBadge,
                      )}
                    >
                      {formatActionLabel(log.actionType)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">
                      {formatEntityType(log.entityType)}
                    </div>
                    {log.entityLabel ? (
                      <div
                        className="text-muted-foreground text-xs truncate max-w-[180px]"
                        title={log.entityLabel}
                      >
                        {log.entityLabel}
                      </div>
                    ) : log.entityId ? (
                      <div className="text-muted-foreground text-xs">
                        {log.entityId.slice(0, 8)}…
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell
                    className="text-sm max-w-xs truncate"
                    title={log.description}
                  >
                    {log.description}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs capitalize">
                      {log.source === "api"
                        ? "Self-service"
                        : (log.source ?? "—")}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={total}
          startIndex={startIndex}
          endIndex={endIndex}
          canGoNext={currentPage < totalPages}
          canGoPrev={currentPage > 1}
          onPageChange={(p) => setCurrentPage(p)}
          onNext={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          onPrev={() => setCurrentPage((p) => Math.max(p - 1, 1))}
        />
      </div>
    </div>
  );
}
